import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Play, Pause, Eye, Trophy, Hash, Clock, Archive, ArchiveRestore, AlertTriangle, Wallet, Radio } from "lucide-react";
import Layout from "../../components/Layout";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

export default function AdminDashboard() {
  const [items, setItems] = useState(null);
  const [archiveFilter, setArchiveFilter] = useState("active");
  const [permDeleteTarget, setPermDeleteTarget] = useState(null);
  const [permDeleteBusy, setPermDeleteBusy] = useState(false);
  const [claimsPending, setClaimsPending] = useState(0);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/challenges?archived=${archiveFilter}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  };

  const loadClaimsStats = async () => {
    try {
      const { data } = await api.get("/admin/prize-claims/stats");
      setClaimsPending(data?.pending || 0);
    } catch (e) {
      /* silent — badge only, admin may not need this */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveFilter]);

  useEffect(() => {
    loadClaimsStats();
  }, []);

  const archive = async (id) => {
    try {
      await api.patch(`/admin/challenges/${id}/archive`, {});
      toast.success("Défi archivé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unarchive = async (id) => {
    try {
      await api.patch(`/admin/challenges/${id}/unarchive`);
      toast.success("Défi désarchivé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/challenges/${id}/status`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce défi ?")) return;
    try {
      await api.delete(`/challenges/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const permanentDelete = async () => {
    if (!permDeleteTarget) return;
    setPermDeleteBusy(true);
    try {
      const { data } = await api.delete(
        `/admin/challenges/${permDeleteTarget.id}/permanent-delete`,
      );
      const c = data?.cascade || {};
      toast.success(
        `Défi supprimé définitivement (codes: ${c.access_codes ?? 0}, sessions: ${c.play_sessions ?? 0}, paiements: ${c.payment_transactions ?? 0})`,
      );
      setPermDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPermDeleteBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat">
              Console admin
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl mt-2 uppercase tracking-tight">
              Mes défis
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="flex items-center gap-1 rounded-full bg-black/40 border border-white/10 p-1"
              data-testid="admin-archive-filter"
            >
              {[
                ["active", "Actifs"],
                ["archived", "Archivés"],
                ["all", "Tous"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  data-testid={`admin-archive-filter-${v}`}
                  onClick={() => setArchiveFilter(v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono-stat uppercase tracking-widest transition-all ${
                    archiveFilter === v
                      ? "bg-cyan-400 text-black"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              data-testid="admin-codes-link"
              onClick={() => navigate("/admin/codes")}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-amber-400/40 hover:text-amber-200 font-display font-bold uppercase text-xs sm:text-sm tracking-widest transition-all"
            >
              <Hash className="w-4 h-4" />
              Codes
            </button>
            <button
              data-testid="admin-prize-claims-link"
              onClick={() => navigate("/admin/prize-claims")}
              className="relative flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:text-emerald-200 font-display font-bold uppercase text-xs sm:text-sm tracking-widest transition-all"
            >
              <Wallet className="w-4 h-4" />
              Gains
              {claimsPending > 0 && (
                <span
                  data-testid="admin-prize-claims-badge"
                  className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-emerald-400 text-black text-[10px] font-mono-stat font-black"
                >
                  {claimsPending}
                </span>
              )}
            </button>
            <button
              data-testid="admin-live-events-link"
              onClick={() => navigate("/admin/live-events")}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-fuchsia-400/40 hover:text-fuchsia-200 font-display font-bold uppercase text-xs sm:text-sm tracking-widest transition-all"
            >
              <Radio className="w-4 h-4" />
              Live
            </button>
            <button
              data-testid="admin-new-challenge-button"
              onClick={() => navigate("/admin/challenges/new")}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase hover:bg-cyan-300 glow-primary active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" />
              Nouveau défi
            </button>
          </div>
        </div>

        {items === null && (
          <div className="text-white/50 font-mono-stat uppercase tracking-widest text-sm">Chargement...</div>
        )}

        {items !== null && items.length === 0 && (
          <div className="panel rounded-2xl p-16 text-center" data-testid="admin-empty-state">
            <h3 className="font-display text-2xl font-bold mb-2">Aucun défi pour l&apos;instant</h3>
            <p className="text-white/60 mb-6">Crée ton premier défi payant pour lancer la plateforme.</p>
            <button
              onClick={() => navigate("/admin/challenges/new")}
              className="px-6 py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase transition-all glow-secondary"
            >
              Créer un premier défi
            </button>
          </div>
        )}

        {items !== null && items.length > 0 && (
          <div className="space-y-4">
            {items.map((c) => {
              const eff = c.status;
              const raw = c.raw_status;
              return (
                <div
                  key={c.id}
                  data-testid={`admin-challenge-${c.id}`}
                  className="panel rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-cyan-300">
                        {c.type === "series" ? "Série" : "Quiz unique"}
                      </span>
                      <StatusBadge effective={eff} raw={raw} />
                    </div>
                    <h3 className="font-display text-xl font-bold truncate">{c.title}</h3>
                    <div className="flex items-center gap-4 text-xs text-white/50 font-mono-stat mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(c.starts_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {" → "}
                        {new Date(c.ends_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span>{c.total_questions} questions</span>
                      <span>{c.price_amount.toFixed(2)} €</span>
                      <span className="flex items-center gap-1 text-amber-300" data-testid={`admin-pool-${c.id}`}>
                        <Trophy className="w-3 h-3" />
                        Cagnotte {c.current_prize_pool?.toFixed(2)} €
                      </span>
                      <span className="flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        {c.codes_count} codes
                      </span>
                      <span>{c.sessions_count} sessions</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {raw === "draft" && (
                      <button
                        data-testid={`admin-publish-${c.id}`}
                        onClick={() => setStatus(c.id, "published")}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/25 transition-all text-sm font-display uppercase tracking-wider"
                      >
                        <Play className="w-3.5 h-3.5" /> Publier
                      </button>
                    )}
                    {raw === "published" && (
                      <button
                        data-testid={`admin-close-${c.id}`}
                        onClick={() => setStatus(c.id, "closed_manually")}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-400/15 border border-amber-400/40 text-amber-300 hover:bg-amber-400/25 transition-all text-sm font-display uppercase tracking-wider"
                      >
                        <Pause className="w-3.5 h-3.5" /> Fermer
                      </button>
                    )}
                    {raw === "closed_manually" && (
                      <button
                        onClick={() => setStatus(c.id, "published")}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-display uppercase tracking-wider"
                      >
                        Réouvrir
                      </button>
                    )}
                    <button
                      data-testid={`admin-view-${c.id}`}
                      onClick={() => navigate(`/admin/challenges/${c.id}`)}
                      className="p-2.5 rounded-lg bg-white/5 hover:bg-cyan-400/20 hover:text-cyan-300 transition-all"
                      title="Détails / codes / classement"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`admin-edit-${c.id}`}
                      onClick={() => navigate(`/admin/challenges/${c.id}/edit`)}
                      className="p-2.5 rounded-lg bg-white/5 hover:bg-fuchsia-500/20 hover:text-fuchsia-300 transition-all"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {c.archived ? (
                      <button
                        data-testid={`admin-unarchive-${c.id}`}
                        onClick={() => unarchive(c.id)}
                        title="Désarchiver"
                        className="p-2.5 rounded-lg bg-white/5 hover:bg-emerald-500/20 hover:text-emerald-300 transition-all"
                      >
                        <ArchiveRestore className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-archive-${c.id}`}
                        onClick={() => archive(c.id)}
                        disabled={c.status !== "finalized"}
                        title={
                          c.status === "finalized"
                            ? "Archiver ce défi"
                            : "Seuls les défis terminés peuvent être archivés."
                        }
                        className="p-2.5 rounded-lg bg-white/5 hover:bg-amber-500/20 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                    {c.archived ? (
                      <button
                        data-testid={`admin-permanent-delete-${c.id}`}
                        onClick={() => setPermDeleteTarget(c)}
                        title="Supprimer définitivement (cascade)"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-pink-500/15 border border-pink-500/40 text-pink-300 hover:bg-pink-500/25 transition-all text-sm font-display uppercase tracking-wider"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Supprimer
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-delete-${c.id}`}
                        onClick={() => remove(c.id)}
                        title="Supprimer (refusé si des codes ont été achetés)"
                        className="p-2.5 rounded-lg bg-white/5 hover:bg-pink-500/20 hover:text-pink-300 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        open={!!permDeleteTarget}
        onOpenChange={(open) => {
          if (!open) setPermDeleteTarget(null);
        }}
      >
        <DialogContent
          data-testid="perm-delete-dialog"
          className="bg-[#0a0a16] border border-pink-500/40 text-white"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-pink-300 font-display uppercase tracking-tight">
              <AlertTriangle className="w-5 h-5" />
              Suppression définitive
            </DialogTitle>
            <DialogDescription className="text-white/70 leading-relaxed pt-2">
              Attention : ce défi archivé{" "}
              <span className="text-white font-semibold">
                « {permDeleteTarget?.title} »
              </span>{" "}
              contient peut-être des codes, paiements, scores, gagnants ou
              réclamations. La suppression est définitive et supprimera tout
              l&apos;historique associé. Confirmer la suppression ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <button
              data-testid="perm-delete-cancel"
              onClick={() => setPermDeleteTarget(null)}
              disabled={permDeleteBusy}
              className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 transition-all disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              data-testid="perm-delete-confirm"
              onClick={permanentDelete}
              disabled={permDeleteBusy}
              className="px-4 py-2 rounded-lg bg-pink-500 hover:bg-pink-400 text-white font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
            >
              {permDeleteBusy ? "Suppression…" : "Confirmer la suppression"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function StatusBadge({ effective, raw }) {
  const map = {
    draft: { c: "bg-white/10 text-white/60 border-white/20", l: "Brouillon" },
    scheduled: { c: "bg-cyan-400/15 text-cyan-300 border-cyan-400/40", l: "Programmé" },
    open: { c: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40", l: "Ouvert" },
    pending_finalization: { c: "bg-orange-400/15 text-orange-300 border-orange-400/40", l: "Finalisation" },
    finalized: { c: "bg-amber-400/15 text-amber-300 border-amber-400/40", l: "Finalisé" },
    closed: { c: "bg-amber-400/15 text-amber-300 border-amber-400/40", l: "Fermé" },
  };
  const v = map[effective] || map.draft;
  return (
    <span className={`text-[10px] uppercase tracking-[0.3em] font-mono-stat px-2 py-0.5 rounded-full border ${v.c}`}>
      {v.l}
    </span>
  );
}
